<template>
  <v-dialog
    :value="open"
    max-width="1100"
    persistent
    content-class="orden-compra-show-dialog-content"
    @input="$emit('close')"
  >
    <v-card v-if="orden" class="orden-compra-show-card">

      <v-card-title>
        Orden de Compra
        <v-spacer />
        <v-btn icon @click="$emit('close')"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-card-text class="orden-compra-show-content pb-2">

        <!-- ── INFO PANEL ── -->
        <div class="show-info-panel mb-3">
          <v-row dense>
            <v-col cols="12" sm="3">
              <div class="info-field">
                <div class="info-label"><v-icon x-small class="mr-1">mdi-pound</v-icon>Número</div>
                <div class="info-value">{{ orden.numeroOrdenCompra || orden.id }}</div>
              </div>
            </v-col>
            <v-col cols="12" sm="3">
              <div class="info-field">
                <div class="info-label"><v-icon x-small class="mr-1">mdi-truck</v-icon>Proveedor</div>
                <div class="info-value">{{ orden.proveedor ? orden.proveedor.nombre : "—" }}</div>
              </div>
            </v-col>
            <v-col cols="12" sm="3">
              <div class="info-field">
                <div class="info-label"><v-icon x-small class="mr-1">mdi-warehouse</v-icon>Depósito</div>
                <div class="info-value">{{ orden.deposito ? orden.deposito.nombre : "—" }}</div>
              </div>
            </v-col>
            <v-col cols="12" sm="3">
              <div class="info-field">
                <div class="info-label"><v-icon x-small class="mr-1">mdi-calendar</v-icon>Fecha</div>
                <div class="info-value">{{ formatFecha(orden.fecha) }}</div>
              </div>
            </v-col>
            <v-col cols="12" sm="3">
              <div class="info-field">
                <div class="info-label">Estado</div>
                <div class="mt-1">
                  <v-chip small dark class="white--text" :color="chipEstadoOrdenColor">{{ labelEstadoOrden }}</v-chip>
                </div>
              </div>
            </v-col>
            <v-col cols="12" sm="3">
              <div class="info-field">
                <div class="info-label">Pago</div>
                <div class="mt-1">
                  <v-chip small dark class="white--text" :color="chipPagoColor">{{ labelEstadoPagoOrdenCompra }}</v-chip>
                </div>
              </div>
            </v-col>
            <v-col v-if="orden.estadoPago === 'PAGADO' && orden.fechaPagoCompletado" cols="12" sm="3">
              <div class="info-field">
                <div class="info-label"><v-icon x-small class="mr-1">mdi-cash-check</v-icon>Fecha pago total</div>
                <div class="info-value">{{ formatFecha(orden.fechaPagoCompletado) }}</div>
              </div>
            </v-col>
            <v-col v-if="orden.totalPagado > 0" cols="6" sm="3">
              <div class="info-field">
                <div class="info-label">Ya pagado</div>
                <div class="info-value success--text">${{ Number(orden.totalPagado || 0).toFixed(2) }}</div>
              </div>
            </v-col>
            <v-col v-if="restoAPagar > 0" cols="6" sm="3">
              <div class="info-field">
                <div class="info-label">Resto a pagar</div>
                <div class="info-value error--text font-weight-bold">${{ restoAPagarFormatted }}</div>
              </div>
            </v-col>
          </v-row>
        </div>

        <!-- ── TABLA ── -->
        <div class="table-fixed-header-wrap">
          <v-data-table
            class="mt-2 movimiento-show-table"
            fixed-header
            height="380"
            :headers="headersItems"
            :items="orden.items || []"
            hide-default-footer
            :items-per-page="-1"
          >
            <template v-slot:item.nombre="{ item }">{{ getProductoNombre(item) }}</template>
            <template v-slot:item.marca="{ item }">{{ item.productoMarca || "—" }}</template>
            <template v-slot:item.modelo="{ item }">{{ item.productoModelo || "—" }}</template>
            <template v-slot:item.tamanio="{ item }">{{ item.productoTamanio || "—" }}</template>
            <template v-slot:item.cantidad="{ item }">{{ item.cantidad }}</template>
            <template v-slot:item.precioUnitario="{ item }">
              <span class="d-inline-flex align-center">
                ${{ Number(item.precioUnitario || 0).toFixed(2) }}
                <v-tooltip v-if="item.precioManual" bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" x-small color="orange darken-2" class="ml-1">mdi-pencil</v-icon>
                  </template>
                  <span>{{ $t('common.precioManualTooltip') }}</span>
                </v-tooltip>
              </span>
            </template>
            <template v-slot:item.precioTotal="{ item }">
              <div class="font-weight-bold">${{ lineTotal(item).toFixed(2) }}</div>
            </template>
            <template slot="no-data">
              <div class="text-center py-4 grey--text">Sin productos</div>
            </template>
          </v-data-table>
        </div>

      </v-card-text>

      <!-- ── ACCIONES ── -->
      <v-card-actions class="orden-compra-show-actions flex-wrap align-center">
        <template v-if="esBorrador">
          <v-btn text color="primary" :loading="loadingAccion" @click="actualizarPrecios">Actualizar precios</v-btn>
          <v-btn color="success" :loading="loadingAccion" @click="dialogConfirmarOrden = true">Confirmar orden de compra</v-btn>
        </template>
        <div v-if="puedeRegistrarRecepcion || puedeRegistrarPago" class="d-flex flex-wrap align-center mr-2" style="gap: 8px;">
          <v-btn v-if="puedeRegistrarRecepcion" color="success" :loading="loadingAccion" @click="dialogRecepcion = true">
            Marcar como Recibido
          </v-btn>
          <v-btn v-if="puedeRegistrarPago" color="primary" :loading="loadingAccion" @click="showPagoModal = true">
            Registrar pago
          </v-btn>
        </div>
        <v-btn v-if="puedeCancelarOrdenCompra" color="error" outlined class="mr-2" :loading="loadingAccion" @click="dialogCancelarOrden = true">
          Cancelar orden
        </v-btn>
        <v-spacer />
        <div v-if="orden" class="footer-total">
          <div v-if="orden.descuento > 0" class="footer-total-sub">
            Desc. {{ orden.descuento }}%&nbsp;
            <span class="error--text">−${{ Number(orden.descuentoMonto || 0).toFixed(2) }}</span>
          </div>
          <div v-if="orden.recargo > 0" class="footer-total-sub">
            Rec. {{ orden.recargo }}%&nbsp;
            <span class="warning--text">+${{ Number(orden.recargoMonto || 0).toFixed(2) }}</span>
          </div>
          <div class="footer-total-amount">
            Total&nbsp;&nbsp;<strong>${{ Number(orden.precioTotal || 0).toFixed(2) }}</strong>
          </div>
        </div>
      </v-card-actions>

      <!-- ── DIALOGS ── -->
      <v-dialog v-model="dialogConfirmarOrden" max-width="520">
        <v-card>
          <v-card-title>Confirmar orden de compra</v-card-title>
          <v-card-text>
            Al confirmar se registra la deuda con el proveedor por el total de la orden (${{ Number(orden.precioTotal || 0).toFixed(2) }}).
            Luego no podrá editar la orden ni actualizar precios. ¿Desea continuar?
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="dialogConfirmarOrden = false">Cancelar</v-btn>
            <v-btn color="success" :loading="loadingAccion" @click="confirmarOrden">Confirmar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="dialogCancelarOrden" max-width="560">
        <v-card>
          <v-card-title>Cancelar orden de compra</v-card-title>
          <v-card-text>
            <p class="mb-2">{{ textoResumenCancelacion }}</p>
            <p v-if="orden.estadoRecepcion === 'RECIBIDA'" class="mb-0 text-caption grey--text text--darken-1">
              Si la mercadería ya ingresó al stock, debe haber cantidad suficiente para descontar; de lo contrario el sistema rechazará la operación.
            </p>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="dialogCancelarOrden = false">Volver</v-btn>
            <v-btn color="error" :loading="loadingAccion" @click="confirmarCancelarOrden">Confirmar cancelación</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="dialogRecepcion" max-width="480">
        <v-card>
          <v-card-title>Marcar como Recibido</v-card-title>
          <v-card-text>
            Se registra el ingreso de mercadería al depósito indicado (se incrementa el stock de cada producto).
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="dialogRecepcion = false">Cancelar</v-btn>
            <v-btn color="success" :loading="loadingAccion" @click="confirmarRecepcion">Marcar como Recibido</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="showPagoModal" max-width="500" persistent>
        <v-card>
          <v-card-title>Registrar pago de la orden de compra</v-card-title>
          <v-card-text>
            <div class="mb-3">
              <p class="mb-1"><strong>Total orden:</strong> ${{ (orden && orden.precioTotal != null) ? Number(orden.precioTotal).toFixed(2) : "0.00" }}</p>
              <p v-if="orden && (orden.totalPagado || 0) > 0" class="mb-1"><strong>Ya pagado:</strong> ${{ Number(orden.totalPagado || 0).toFixed(2) }}</p>
              <p class="mb-2"><strong>Resto a pagar:</strong> ${{ restoAPagarFormatted }}</p>
            </div>
            <div class="mb-2 font-weight-medium">Medios de pago:</div>
            <div v-for="(item, index) in mediosDePago" :key="index" class="d-flex align-center mb-2" style="gap: 8px;">
              <v-select v-model="item.medio" :items="mediosPagoOptions" outlined dense hide-details class="flex-grow-1" />
              <v-text-field v-model="item.monto" type="number" prefix="$" outlined dense hide-details class="flex-grow-1" style="max-width: 140px;" min="0" step="0.01" />
              <v-btn v-if="mediosDePago.length > 1" icon small color="error" @click="quitarMedioDePago(index)">
                <v-icon small>mdi-minus</v-icon>
              </v-btn>
            </div>
            <div class="d-flex align-center justify-space-between mt-1 mb-2">
              <v-btn small text color="primary" @click="agregarMedioDePago">
                <v-icon small left>mdi-plus</v-icon> Agregar medio de pago
              </v-btn>
              <v-btn v-if="mediosDePago.length === 1" small outlined color="primary" @click="pagarTotal">
                <v-icon small left>mdi-cash-check</v-icon> Pagar total
              </v-btn>
            </div>
            <v-alert v-if="pagoMontoError" type="error" dense outlined class="mt-2">{{ pagoMontoError }}</v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="showPagoModal = false">Cancelar</v-btn>
            <v-btn color="primary" :disabled="!!pagoMontoError || (restoAPagar > 0 && sumaMediosDePago <= 0)" :loading="loadingPago" @click="registrarPago">Registrar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-snackbar v-model="snackbarError" color="error" :timeout="12000" top>
        <span style="white-space: pre-line;">{{ errorMsg }}</span>
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbarError = false">Cerrar</v-btn>
        </template>
      </v-snackbar>
    </v-card>

    <v-card v-else>
      <v-card-text class="text-center">
        <v-progress-circular indeterminate color="primary" />
        <p class="mt-2">Cargando orden de compra...</p>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import OrdenCompraProvider from "../../providers/OrdenCompraProvider";
import { formatFechaVentas } from "../../../ventas/utils/dateFormat";

export default {
  name: "OrdenCompraShow",
  props: {
    open: { type: Boolean, default: true },
    ordenCompraId: { type: String, default: null },
  },
  data() {
    return {
      orden: null,
      loadingAccion: false,
      loadingPago: false,
      dialogRecepcion: false,
      dialogCancelarOrden: false,
      dialogConfirmarOrden: false,
      showPagoModal: false,
      mediosDePago: [{ medio: "Efectivo", monto: 0 }],
      mediosPagoOptions: ["Efectivo", "Tarjeta Débito", "Tarjeta Crédito", "Transferencia"],
      snackbarError: false,
      errorMsg: "",
      headersItems: [
        { text: "Producto", value: "nombre", sortable: false },
        { text: "Marca", value: "marca", sortable: false },
        { text: "Modelo", value: "modelo", sortable: false },
        { text: "Tamaño", value: "tamanio", sortable: false },
        { text: "Cant.", value: "cantidad", sortable: false },
        { text: "P. unit.", value: "precioUnitario", sortable: false },
        { text: "Total línea", value: "precioTotal", sortable: false },
      ],
    };
  },
  computed: {
    ...mapGetters(["me"]),
    esBorrador() { return this.orden && this.orden.estadoOrdenCompra === "BORRADOR"; },
    puedeCancelarOrdenCompra() {
      if (!this.orden || this.orden.estadoOrdenCompra === "CANCELADA") return false;
      const perms = this.me && this.me.role && this.me.role.permissions;
      return Array.isArray(perms) && perms.includes("COMPRAS_ORDEN_CANCELAR");
    },
    textoResumenCancelacion() {
      if (!this.orden) return "";
      const partes = [];
      if (this.orden.estadoOrdenCompra === "BORRADOR") {
        partes.push("La orden quedará marcada como cancelada (no había deuda ni movimientos de stock asociados).");
      } else {
        partes.push("Se anulará la deuda registrada con el proveedor por esta orden.");
        if (this.orden.estadoRecepcion === "RECIBIDA") partes.push("Se descontará del stock la mercadería recibida.");
        if (Number(this.orden.totalPagado || 0) > 0) {
          partes.push("Se revertirán los pagos en cuenta proveedor y se registrará un movimiento de ingreso por el monto devuelto (reembolso de orden de compra).");
        }
      }
      partes.push("Esta acción no se puede deshacer.");
      return partes.join(" ");
    },
    labelEstadoOrden() {
      if (!this.orden) return "—";
      return { BORRADOR: "Borrador", CONFIRMADA: "Confirmada", RECIBIDA: "Recibida", CANCELADA: "Cancelada" }[this.orden.estadoOrdenCompra] || this.orden.estadoOrdenCompra || "—";
    },
    chipEstadoOrdenColor() {
      if (!this.orden) return "grey";
      return { BORRADOR: "grey darken-1", CONFIRMADA: "primary", RECIBIDA: "success", CANCELADA: "red darken-2" }[this.orden.estadoOrdenCompra] || "grey";
    },
    puedeRegistrarRecepcion() { return this.orden && this.orden.estadoOrdenCompra === "CONFIRMADA"; },
    puedeRegistrarPago() {
      if (!this.orden || this.orden.estadoPago === "PAGADO") return false;
      const e = this.orden.estadoOrdenCompra;
      return e === "CONFIRMADA" || e === "RECIBIDA";
    },
    labelEstadoPagoOrdenCompra() {
      if (!this.orden) return "—";
      return { PAGADO: "Pagada", PAGO_PARCIAL: "Pago parcial", NO_PAGADO: "Impaga" }[this.orden.estadoPago] || this.orden.estadoPago || "—";
    },
    chipPagoColor() {
      if (!this.orden) return "grey";
      return { PAGADO: "success", PAGO_PARCIAL: "amber darken-2", NO_PAGADO: "error" }[this.orden.estadoPago] || "grey";
    },
    sumaMediosDePago() {
      return (this.mediosDePago || []).reduce((acc, p) => acc + (Number(p.monto) || 0), 0);
    },
    restoAPagar() {
      if (!this.orden) return 0;
      const rest = this.orden.restoAPagar;
      if (rest != null) return Number(rest);
      return Math.round((Number(this.orden.precioTotal || 0) - Number(this.orden.totalPagado || 0)) * 100) / 100;
    },
    restoAPagarFormatted() { return this.restoAPagar.toFixed(2); },
    pagoMontoError() {
      const total = this.sumaMediosDePago;
      if (total <= 0) return "";
      if (total > this.restoAPagar) return `No puede superar lo que falta por pagar ($${this.restoAPagarFormatted})`;
      return "";
    },
  },
  watch: {
    open(v) { if (v && this.ordenCompraId) this.load(); else this.orden = null; },
    ordenCompraId(v) { if (v && this.open) this.load(); },
    showPagoModal(v) { if (v) this.mediosDePago = [{ medio: "Efectivo", monto: 0 }]; },
  },
  mounted() { if (this.open && this.ordenCompraId) this.load(); },
  methods: {
    formatFecha: formatFechaVentas,
    getProductoNombre(item) {
      return item.productoNombre || `${item.productoMarca || ""} ${item.productoModelo || ""}`.trim() || "—";
    },
    lineTotal(item) {
      if (item.precioFinalLinea != null) return Number(item.precioFinalLinea);
      if (item.precioTotal != null) return Number(item.precioTotal);
      return (Number(item.precioUnitario) || 0) * (Number(item.cantidad) || 0);
    },
    async load() {
      if (!this.ordenCompraId) return;
      try {
        const r = await OrdenCompraProvider.findOrdenCompra(this.ordenCompraId);
        this.orden = (r.data && r.data.ordenCompraFind) || null;
      } catch (e) { console.error(e); }
    },
    async actualizarPrecios() {
      this.loadingAccion = true; this.errorMsg = "";
      try { await OrdenCompraProvider.actualizarPrecios(this.ordenCompraId); await this.load(); this.$emit("refreshed"); }
      catch (e) { this.errorMsg = e?.graphQLErrors?.[0]?.message || e?.message || "Error al actualizar precios."; this.snackbarError = true; }
      finally { this.loadingAccion = false; }
    },
    async confirmarOrden() {
      this.loadingAccion = true; this.errorMsg = "";
      try { await OrdenCompraProvider.confirmarOrdenCompra(this.ordenCompraId); this.dialogConfirmarOrden = false; await this.load(); this.$emit("refreshed"); }
      catch (e) { this.errorMsg = e?.graphQLErrors?.[0]?.message || e?.message || "Error al confirmar la orden."; this.snackbarError = true; }
      finally { this.loadingAccion = false; }
    },
    async confirmarRecepcion() {
      this.loadingAccion = true; this.errorMsg = "";
      try { await OrdenCompraProvider.registrarRecepcion(this.ordenCompraId); this.dialogRecepcion = false; await this.load(); this.$emit("refreshed"); }
      catch (e) { this.errorMsg = e?.graphQLErrors?.[0]?.message || e?.message || "Error al marcar como recibido."; this.snackbarError = true; }
      finally { this.loadingAccion = false; }
    },
    async confirmarCancelarOrden() {
      this.loadingAccion = true; this.errorMsg = "";
      try { await OrdenCompraProvider.cancelarOrdenCompra(this.ordenCompraId); this.dialogCancelarOrden = false; await this.load(); this.$emit("refreshed"); }
      catch (e) { this.errorMsg = e?.graphQLErrors?.[0]?.message || e?.message || "Error al cancelar la orden."; this.snackbarError = true; }
      finally { this.loadingAccion = false; }
    },
    agregarMedioDePago() { this.mediosDePago.push({ medio: "Efectivo", monto: 0 }); },
    quitarMedioDePago(index) { if (this.mediosDePago.length > 1) this.mediosDePago.splice(index, 1); },
    pagarTotal() {
      const medio = (this.mediosDePago[0] && this.mediosDePago[0].medio) || "Efectivo";
      this.mediosDePago = [{ medio, monto: this.restoAPagar }];
    },
    async registrarPago() {
      if (!this.ordenCompraId) return;
      if (this.restoAPagar <= 0) {
        this.loadingPago = true; this.errorMsg = "";
        try {
          const medio = (this.mediosDePago[0] && this.mediosDePago[0].medio) || "Efectivo";
          await OrdenCompraProvider.registrarPago(this.ordenCompraId, 0, medio);
          this.showPagoModal = false;
          this.mediosDePago = [{ medio: "Efectivo", monto: 0 }];
          await this.load();
          this.$emit("refreshed");
        } catch (e) { this.errorMsg = e?.graphQLErrors?.[0]?.message || e?.message || "Error al registrar el pago."; this.snackbarError = true; }
        finally { this.loadingPago = false; }
        return;
      }
      const pagos = (this.mediosDePago || []).filter((p) => Number(p.monto) > 0);
      if (!pagos.length || this.sumaMediosDePago > this.restoAPagar) return;
      this.loadingPago = true; this.errorMsg = "";
      try {
        for (const pago of pagos) await OrdenCompraProvider.registrarPago(this.ordenCompraId, Number(pago.monto), pago.medio || "Efectivo");
        this.showPagoModal = false;
        this.mediosDePago = [{ medio: "Efectivo", monto: 0 }];
        await this.load();
        this.$emit("refreshed");
      } catch (e) { this.errorMsg = e?.graphQLErrors?.[0]?.message || e?.message || "Error al registrar el pago."; this.snackbarError = true; }
      finally { this.loadingPago = false; }
    },
  },
};
</script>

<style scoped>
::v-deep .orden-compra-show-dialog-content {
  height: 90vh;
  max-height: 90vh;
}
.orden-compra-show-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.orden-compra-show-content {
  flex: 1 1 auto;
  overflow-y: auto;
}
.orden-compra-show-actions {
  flex: 0 0 auto;
  border-top: 1px solid #e0e0e0;
}
.table-fixed-header-wrap { min-height: 0; }
.movimiento-show-table { border-radius: 8px; overflow: hidden; }

/* Info panel */
.show-info-panel {
  background: #f8f9fc;
  border-radius: 8px;
  border: 1px solid #e8eaf0;
  padding: 12px 16px;
}
.info-field { padding: 2px 0; }
.info-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #90a4ae;
  font-weight: 600;
  display: flex;
  align-items: center;
}
.info-value {
  font-size: 0.92rem;
  font-weight: 500;
  color: #37474f;
  margin-top: 2px;
}

/* Footer total */
.footer-total {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.3;
}
.footer-total-sub { font-size: 0.85rem; color: #90a4ae; }
.footer-total-amount { font-size: 1.25rem; color: #1565c0; font-weight: 600; }

::v-deep .v-chip { color: #fff !important; }
::v-deep .v-chip .v-chip__content { color: #fff !important; }
</style>
